import {
  Box,
  Button,
  Flex,
  Icon,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiChevronRight } from 'react-icons/fi';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import {
  listSnapshots,
  resendSnapshotInvite,
  type SnapshotListItem,
} from 'services/snapshotsService';
import {
  NewSnapshotButton,
  SnapshotPageShell,
  SnapshotPageSpinner,
  SnapshotSectionCard,
  SnapshotStatusTag,
  SnapshotTableColumnHeader,
  useSnapshotTableStyles,
} from './components/snapshotUi';
import {
  canOpenSnapshotDetail,
  formatSnapshotDay,
  statusColors,
  statusLabel,
} from './snapshotUtils';

const PAGE_SIZE = 6;

export function SnapshotsList() {
  const { token } = useAuth() || {};
  const navigate = useNavigate();
  const toast = useToast();
  const { borderColor, textColor, cellProps, rowHoverBg } = useSnapshotTableStyles();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<SnapshotListItem[]>([]);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await listSnapshots(token);
      setRows(data);
      setPageIndex(0);
    } catch {
      toast({ title: 'Could not load snapshots', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = useMemo(() => {
    const start = pageIndex * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, pageIndex]);

  const handleResend = async (id: string) => {
    if (!token) return;
    setResendingId(id);
    try {
      await resendSnapshotInvite(token, id);
      toast({ title: 'Invite resent', status: 'success' });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Could not resend invite';
      toast({ title: msg, status: 'error' });
    } finally {
      setResendingId(null);
    }
  };

  const expiresCell = (row: SnapshotListItem) => {
    if (row.status === 'waiting_on_partner' || row.status === 'waiting_on_you') {
      return `Invited ${formatSnapshotDay(row.invitedAt || row.createdAt)}`;
    }
    return formatSnapshotDay(row.expiresAt);
  };

  const overlapCell = (row: SnapshotListItem) => {
    if (!row.overlap) return '-';
    const { viewerRate, rateMin, rateMax } = row.overlap;
    if (viewerRate != null) return `${viewerRate}% of your list`;
    if (rateMin === rateMax) return `${rateMin}% of your list`;
    return `${rateMin}%–${rateMax}% of each list`;
  };

  const paginationBorder = useColorModeValue('gray.200', 'white');

  return (
    <SnapshotPageShell>
      <SnapshotSectionCard>
        <Flex
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap={4}
          mb={4}
        >
          <Box flex={1} minW={0}>
            <Text fontSize="2xl" fontWeight="bold" color={textColor}>
              Snapshots
            </Text>
            <Text color="gray.500" fontSize="md" lineHeight="1.6" mt={2} maxW="720px">
              See where your accounts overlap with any company. Invite them by email and
              share a list. No HubSpot connection required.
            </Text>
            <Text color="gray.500" fontSize="sm" mt={2} maxW="720px" lineHeight="1.5">
              Uploaded files are removed after 24 hours. Overlap results are available for
              14 days.
            </Text>
          </Box>
          <NewSnapshotButton onClick={() => navigate('/admin/snapshots/new')} />
        </Flex>

        {loading ? (
          <SnapshotPageSpinner />
        ) : rows.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={12}
            px={6}
            textAlign="center"
          >
            <Text fontSize="lg" fontWeight="600" color={textColor} mb={2}>
              No snapshots yet
            </Text>
            <Text fontSize="md" color="gray.500" maxW="400px" lineHeight="1.6" mb={6}>
              Invite a company, upload your account lists, and see where you overlap. You
              do not need HubSpot to get started.
            </Text>
            <NewSnapshotButton onClick={() => navigate('/admin/snapshots/new')} />
          </Flex>
        ) : (
          <Flex direction="column" w="100%" overflowX={{ sm: 'scroll' }}>
            <Table variant="simple" color="gray.500" mb="24px">
              <Thead>
                <Tr>
                  <Th pe="10px" borderColor={borderColor}>
                    <SnapshotTableColumnHeader>COMPANY</SnapshotTableColumnHeader>
                  </Th>
                  <Th pe="10px" borderColor={borderColor}>
                    <SnapshotTableColumnHeader>STATUS</SnapshotTableColumnHeader>
                  </Th>
                  <Th pe="10px" borderColor={borderColor}>
                    <SnapshotTableColumnHeader>OVERLAP</SnapshotTableColumnHeader>
                  </Th>
                  <Th pe="10px" borderColor={borderColor}>
                    <SnapshotTableColumnHeader>EXPIRES</SnapshotTableColumnHeader>
                  </Th>
                  <Th pe="10px" borderColor={borderColor} w="140px" />
                </Tr>
              </Thead>
              <Tbody>
                {pageRows.map((row) => {
                  const colors = statusColors(row.status);
                  const canOpen = canOpenSnapshotDetail(row.status);
                  return (
                    <Tr
                      key={row.id}
                      _hover={
                        canOpen
                          ? { bg: rowHoverBg, cursor: 'pointer' }
                          : { bg: rowHoverBg }
                      }
                      transition="all 0.2s"
                      onClick={() => canOpen && navigate(`/admin/snapshots/${row.id}`)}
                    >
                      <Td {...cellProps}>
                        <Text
                          color={textColor}
                          fontSize="md"
                          fontWeight="500"
                          maxW="200px"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {row.partnerCompanyName}
                        </Text>
                        {row.partnerCompanyDomain && (
                          <Text fontSize="sm" color="gray.500" mt={0.5}>
                            {row.partnerCompanyDomain}
                          </Text>
                        )}
                      </Td>
                      <Td {...cellProps}>
                        <SnapshotStatusTag
                          label={statusLabel(row.status, row.partnerCompanyName)}
                          bg={colors.bg}
                          color={colors.color}
                        />
                      </Td>
                      <Td {...cellProps}>
                        <Text color={textColor} fontSize="md" fontWeight="500">
                          {overlapCell(row)}
                        </Text>
                      </Td>
                      <Td {...cellProps}>
                        <Text color={textColor} fontSize="md" fontWeight="500">
                          {expiresCell(row)}
                        </Text>
                      </Td>
                      <Td {...cellProps} textAlign="right">
                        {row.status === 'waiting_on_partner' && row.isInitiator && (
                          <Button
                            size="sm"
                            variant="ghost"
                            colorScheme="purple"
                            fontWeight="600"
                            isLoading={resendingId === row.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResend(row.id);
                            }}
                          >
                            Resend invite
                          </Button>
                        )}
                        {row.status === 'waiting_on_you' && (
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="purple"
                            fontWeight="600"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/snapshots/${row.id}`);
                            }}
                          >
                            Upload your list
                          </Button>
                        )}
                        {row.status === 'expiring_soon' && row.isInitiator && (
                          <Button
                            size="sm"
                            variant="ghost"
                            colorScheme="orange"
                            fontWeight="600"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/admin/snapshots/new', {
                                state: {
                                  partnerCompanyName: row.partnerCompanyName,
                                  partnerCompanyDomain: row.partnerCompanyDomain,
                                },
                              });
                            }}
                          >
                            Request refresh
                          </Button>
                        )}
                        {canOpen && (
                          <Icon as={FiChevronRight} boxSize={5} color="gray.400" />
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>

            {rows.length > PAGE_SIZE && (
              <Flex w="100%" justify="space-between" px="20px" pt="10px" pb="5px" align="center">
                <Text fontSize="sm" color="gray.500" fontWeight="normal">
                  Showing {pageIndex * PAGE_SIZE + 1} to{' '}
                  {Math.min(pageIndex * PAGE_SIZE + PAGE_SIZE, rows.length)} of {rows.length}{' '}
                  entries
                </Text>
                <Stack direction="row" spacing="4px">
                  <Button
                    variant="no-effects"
                    onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                    disabled={pageIndex === 0}
                    w="40px"
                    h="40px"
                    borderRadius="50%"
                    bg="transparent"
                    border="1px solid"
                    borderColor={paginationBorder}
                    _hover={{ bg: 'whiteAlpha.100', opacity: '0.7' }}
                  >
                    <Icon as={MdChevronLeft} w="16px" h="16px" color={textColor} />
                  </Button>
                  {Array.from({ length: pageCount }, (_, i) => i + 1).map((pageNumber) => (
                    <Button
                      key={pageNumber}
                      variant="no-effects"
                      onClick={() => setPageIndex(pageNumber - 1)}
                      w="40px"
                      h="40px"
                      borderRadius="50%"
                      bg={pageNumber === pageIndex + 1 ? 'brand.50' : 'transparent'}
                      border={
                        pageNumber === pageIndex + 1 ? 'none' : '1px solid lightgray'
                      }
                      _hover={
                        pageNumber === pageIndex + 1
                          ? { opacity: '0.7' }
                          : { bg: 'whiteAlpha.100' }
                      }
                    >
                      <Text
                        fontSize="sm"
                        color={pageNumber === pageIndex + 1 ? 'black' : textColor}
                      >
                        {pageNumber}
                      </Text>
                    </Button>
                  ))}
                  <Button
                    variant="no-effects"
                    onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
                    disabled={pageIndex >= pageCount - 1}
                    w="40px"
                    h="40px"
                    borderRadius="50%"
                    bg="transparent"
                    border="1px solid"
                    borderColor={paginationBorder}
                    _hover={{ bg: 'whiteAlpha.100', opacity: '0.7' }}
                  >
                    <Icon as={MdChevronRight} w="16px" h="16px" color={textColor} />
                  </Button>
                </Stack>
              </Flex>
            )}
          </Flex>
        )}
      </SnapshotSectionCard>
    </SnapshotPageShell>
  );
}
